import { FC, useCallback } from "react";
import {
    PaymentForm,
    ProductOrderItem,
    TaskSaleAdditional,
} from "tm-shared/types";

import BaseButton from "@/components/BaseButton";
import { Loading } from "@/components/Loading";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { GeneralProductCard } from "@/pages/WorkPlan/Tasks/components/GeneralProductCard";
import { useCreateCheckout } from "@/pages/WorkPlan/Tasks/hooks";
import { AdvancedProduct } from "@/pages/WorkPlan/Tasks/SalePage/SalePage.types";
import { setSaleProduct } from "@/store/slices/progress";

import {
    GoodsWrapper,
    MainContainer,
    StyledCartBtnContainer,
} from "./ProductsFormCatalog.styled";

interface ProductsFormCatalogProps {
    task: TaskSaleAdditional;
    marketId: string;
    products: AdvancedProduct[];
    isOnlyPlan: boolean;
    paymentForm: PaymentForm;
    payloadProducts: Record<string, ProductOrderItem>;
    loading: boolean;
    ignoreRemainings: string[];
}

const BATCH_SIZE = 20;

export const ProductsFormCatalog: FC<ProductsFormCatalogProps> = ({
    marketId,
    task,
    products,
    isOnlyPlan,
    paymentForm,
    payloadProducts,
    ignoreRemainings,
    loading,
}) => {
    const dispatch = useAppDispatch();

    const { inventoryList } = useAppSelector((state) => state.offline);

    const { markets } = useAppSelector((state) => state.work);

    const currentMarket = markets?.[marketId];
    const currentMarketInventory = inventoryList?.[currentMarket?.storage?.id];

    const { visibleItems: visibleProducts, loadMore } = useInfiniteScroll(
        isOnlyPlan
            ? products.filter((p) => p.productPlan || p.additionalPlan)
            : products,
        BATCH_SIZE,
    );

    const taskId = task.id;

    const { handle: handleSendProductsToCheckout, isLoading: completeLoading } =
        useCreateCheckout({
            task,
            marketId,
            paymentForm,
            products: payloadProducts,
            shouldIgnoreRemainings: ignoreRemainings.length > 0,
        });

    const handleProductCountChange = useCallback(
        (product: ProductOrderItem) => {
            dispatch(
                setSaleProduct({
                    taskId,
                    product,
                }),
            );
        },
        [dispatch, marketId, markets, task.context.route, taskId],
    );

    const completeVisible = Object.values(payloadProducts).length > 0;

    return (
        <MainContainer id="catalog-container">
            {(loading || completeLoading) && <Loading />}
            <GoodsWrapper>
                {visibleProducts.map((item, index, filteredArray) => {
                    const isLast = index === filteredArray.length - 1;
                    const currentCount =
                        payloadProducts[item.barcode]?.count ?? 0;
                    const isProductRemainingIgnored = ignoreRemainings.includes(
                        item.barcode,
                    );

                    return (
                        <GeneralProductCard
                            key={item.barcode}
                            {...item}
                            itemsLeft={currentMarketInventory?.[item.barcode]}
                            hasSelectedPaymentForm={!!paymentForm}
                            value={currentCount}
                            taskId={taskId}
                            marketId={marketId}
                            isLast={isLast}
                            onInView={() => loadMore()}
                            productPlan={item.productPlan}
                            additionalPlan={item.additionalPlan}
                            onCountChange={handleProductCountChange}
                            isRemainingIgnored={ignoreRemainings.length > 0}
                            isProductRemainingIgnored={
                                isProductRemainingIgnored
                            }
                        />
                    );
                })}
            </GoodsWrapper>

            {completeVisible && (
                <StyledCartBtnContainer>
                    <BaseButton
                        type="primary"
                        loading={completeLoading}
                        onClick={handleSendProductsToCheckout}
                    >
                        Кошик
                    </BaseButton>
                </StyledCartBtnContainer>
            )}
        </MainContainer>
    );
};
